import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, Bot, Eye } from 'lucide-react';
import './style/ExamDetails.css';
import { getExamData } from './api';

export default function ExamDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [examData, setExamData] = useState(null);
  const [studentsData, setStudentsData] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [selectedEvaluation, setSelectedEvaluation] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExam = async (examId) => {
      try {
        setLoading(true);
        const response = await getExamData(examId);
        console.log("API Response:", response);

        if (response.statusCode === 200 && response.body) {
          const parsedBody = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
          console.log("Parsed body:", parsedBody);

          if (parsedBody.data) {
            setExamData(parsedBody.data);
            processStudentData(parsedBody.data.examSubmissions || []);
          } else {
            throw new Error('No data found in the response');
          }
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err) {
        console.error('Error fetching exam data:', err);
        setError(`Failed to load exam details: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchExam(id);
  }, [id]);

  const processStudentData = (submissions) => {
    const students = submissions.map((submission) => ({
      id: submission.submissionId,
      submissionId: submission.submissionId,
      studentId: submission.studentId,
      name: submission.studentName,
      status: submission.status,
      marks: submission.obtainedMarks,
      startDate: submission.startTime,
      endDate: submission.endTime,
      evaluated: submission.status === 'evaluated'
    }));
    console.log("Processed students data:", students);
    setStudentsData(students);
  };

  const handleEvaluateAI = (submissionId) => {
    navigate(`/exam/evaluate/${id}/${submissionId}`);
  };

  const handleViewResponse = (submissionId) => {
    navigate(`/exam/submission/${id}/${submissionId}`);
  };

  const totalStudents = studentsData.length;
  const completedExams = studentsData.filter(student => student.status === 'submitted').length;
  const inProgressExams = studentsData.filter(student => student.status === 'in progress').length;
  const averageMarks = studentsData.reduce((sum, student) => sum + (student.marks || 0), 0) / completedExams || 0;

  const filteredStudents = studentsData
    .filter(student => student.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(student => selectedStatus === 'All' || student.status === selectedStatus)
    .filter(student => selectedEvaluation === 'All' || 
      (selectedEvaluation === 'Evaluated' && student.evaluated) || 
      (selectedEvaluation === 'Not Evaluated' && !student.evaluated));

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  if (loading) {
    return <div className="unique18-loading">Loading...</div>;
  }

  if (error) {
    return <div className="unique18-error">{error}</div>;
  }

  return (
    <div className="unique18-container bg-gradient-to-br from-blue-100 to-purple-100 min-h-screen">
      <div className="unique18-header">
        <h1 className="unique18-title">Exam {examData?.exam_topic || id} Details</h1>
        <div className="unique18-title-underline"></div>
      </div>

      <div className="unique18-stats-grid">
        <div className="unique18-stat-card">
          <div className="unique18-stat-icon">👥</div>
          <h2 className="unique18-stat-title">Total Students</h2>
          <p className="unique18-stat-value">{totalStudents}</p>
        </div>
        <div className="unique18-stat-card">
          <div className="unique18-stat-icon">✅</div>
          <h2 className="unique18-stat-title">Completed Exams</h2>
          <p className="unique18-stat-value">{completedExams}</p>
        </div>
        <div className="unique18-stat-card">
          <div className="unique18-stat-icon">⏳</div>
          <h2 className="unique18-stat-title">In Progress</h2>
          <p className="unique18-stat-value">{inProgressExams}</p>
        </div>
        <div className="unique18-stat-card">
          <div className="unique18-stat-icon">🏆</div>
          <h2 className="unique18-stat-title">Average Marks</h2>
          <p className="unique18-stat-value">{averageMarks.toFixed(2)}</p>
        </div>
      </div>

      <div className="unique18-filters">
        <div className="unique18-search-container">
          <input
            type="text"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="unique18-search-input"
          />
          <Search className="unique18-search-icon" size={20} />
        </div>
        <div className="unique18-status-filter">
          <label htmlFor="status-filter" className="unique18-filter-label">Filter by Status:</label>
          <select 
            id="status-filter" 
            value={selectedStatus} 
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="unique18-select"
          >
            <option value="All">All</option>
            <option value="submitted">Completed</option>
            <option value="in progress">In Progress</option>
            <option value="not started">Not Started</option>
          </select>
        </div>
        <div className="unique18-status-filter">
          <label htmlFor="evaluation-filter" className="unique18-filter-label">Filter by Evaluation:</label>
          <select 
            id="evaluation-filter" 
            value={selectedEvaluation} 
            onChange={(e) => setSelectedEvaluation(e.target.value)}
            className="unique18-select"
          >
            <option value="All">All</option>
            <option value="Evaluated">Evaluated</option>
            <option value="Not Evaluated">Not Evaluated</option>
          </select>
        </div>
      </div>

      <div className="unique18-table-container">
        <table className="unique18-table">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Name</th>
              <th>Status</th>
              <th>Marks Obtained</th>
              <th>Start Date</th>
              <th>Submission Date</th>
              <th>Evaluated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((student, index) => (
              <tr key={student.submissionId} className={`unique18-table-row unique18-status-${student.status.toLowerCase().replace(' ', '-')}`}>
                <td>{index + 1}</td>
                <td>{student.name}</td>
                <td>{student.status}</td>
                <td>{student.marks || 'N/A'}</td>
                <td>{formatDate(student.startDate)}</td>
                <td>{formatDate(student.endDate)}</td>
                <td>{student.evaluated ? 'Yes' : 'No'}</td>
                <td>
                  <div className="unique18-action-buttons">
                    <button 
                      className="unique18-action-button unique18-evaluate-button" 
                      onClick={() => handleEvaluateAI(student.submissionId)}
                    >
                      <Bot size={16} className="unique18-button-icon" />
                      Evaluate AI
                    </button>
                    <button 
                      className="unique18-action-button unique18-view-button" 
                      onClick={() => handleViewResponse(student.submissionId)}
                    >
                      <Eye size={16} className="unique18-button-icon" />
                      View Response
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}